import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAsana } from 'services/asana/getAsana';
import { completeAsana } from 'services/asana/completeAsana';
import { Box, Typography, Button } from '@mui/material';
import toast from 'react-hot-toast';
import { sendReport } from 'services/report/sendReport';

function ContinuousFlow() {
  const { courseid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const studentid = localStorage.getItem('studentid');
  const parsedStudentId = studentid ? Number(studentid) : null;
  const [currentAsanaIndex, setCurrentAsanaIndex] = useState(0);
  const [completedAsanas, setCompletedAsanas] = useState(() => {
    if (!parsedStudentId || !courseid) return [];
    const saved = localStorage.getItem(`completedAsanas_${courseid}_${parsedStudentId}`);
    console.log('Loaded completedAsanas from localStorage:', saved ? JSON.parse(saved) : 'None');
    return saved ? JSON.parse(saved) : [];
  });
  const [isTracking, setIsTracking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const processedAsanasRef = useRef(new Set());

  const sendReportMutation = useMutation({
    mutationFn: sendReport,
    onSuccess: (data) => {
      console.log('sendReport response:', data);
      if (data?.status) toast.success('Report created successfully');
      else toast.error(data?.message || 'Failed to create report');
    },
    onError: (error) => {
      console.error('sendReportMutation error:', error);
      toast.error(error?.response?.data?.message || error.message || 'Error sending report');
    }
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryFn: () => getAsana({ courseId: Number(courseid), studentId: parsedStudentId }),
    queryKey: ['asanas', Number(courseid), parsedStudentId],
    enabled: !!parsedStudentId,
    onSuccess: (data) => {
      console.log(
        'Fetched asanas:',
        data?.response?.map((asana) => ({
          id: asana.id,
          posturename: asana.posturename,
          completed: asana.completed,
          posturenumber: asana.posturenumber,
          sessionData: asana.sessionData || 'No session data'
        })) || 'No response data'
      );
    }
  });

  const yogaAsanas = (data?.response || []).sort((a, b) => a.posturenumber - b.posturenumber);

  const completeAsanaMutation = useMutation({
    mutationFn: completeAsana,
    onSuccess: async (data) => {
      console.log('completeAsanaMutation success, invalidating queries');
      await queryClient.invalidateQueries(['asanas', Number(courseid), parsedStudentId]);
      await refetch();
      // Update completedAsanas with asanaimage
      setCompletedAsanas((prev) => {
        const updated = prev.map((asana) =>
          asana.id === Number(data.postureId)
            ? { ...asana, sessionData: { ...asana.sessionData, asanaimage: data.asanaimage || asana.sessionData.asanaimage } }
            : asana
        );
        localStorage.setItem(`completedAsanas_${courseid}_${parsedStudentId}`, JSON.stringify(updated));
        return updated;
      });
    },
    onError: (error) => {
      console.error('completeAsanaMutation error:', error);
      toast.error(error.message || 'Failed to mark asana as completed');
    }
  });

  const navigateToNextAsana = () => {
    console.log('navigateToNextAsana called', {
      currentAsanaIndex,
      completedAsanasCount: completedAsanas.length,
      processedAsanas: Array.from(processedAsanasRef.current),
      completedAsanas
    });

    if (yogaAsanas.every((asana) => asana.completed === 1) && completedAsanas.length > 0) {
      console.log('All asanas completed, navigating to summary with', completedAsanas.length, 'asanas');
      const allSessionData = completedAsanas
        .filter((asana) => asana.sessionData.averageAccuracy > 0)
        .map((asana) => ({
          poseName: asana.posturename,
          averageAccuracy: Number(asana.sessionData.averageAccuracy),
          captureDuration: Number(asana.sessionData.captureDuration),
          frameCount: Number(asana.sessionData.frameCount),
          goodPostureTime: Number(asana.sessionData.goodPostureTime),
          studentId: Number(asana.sessionData.studentId || parsedStudentId),
          courseId: Number(asana.sessionData.courseId || courseid),
          poseId: Number(asana.sessionData.poseId || asana.id),
          asanaimage: asana.sessionData.asanaimage
        }));
      console.log('Navigating to summary with allSessionData:', allSessionData);
      navigate(`/asanas/${courseid}/summary`, {
        state: {
          allSessionData,
          course: { courseName: yogaAsanas[0]?.course_name || 'Yoga Course' }
        }
      });
      return;
    }

    const currentAsana = yogaAsanas[currentAsanaIndex];
    if (currentAsana && currentAsana.completed !== 1 && !processedAsanasRef.current.has(currentAsana.id)) {
      console.log('Navigating to pose-tracking for asana:', {
        id: currentAsana.id,
        posturename: currentAsana.posturename,
        index: currentAsanaIndex,
        completed: currentAsana.completed
      });
      setIsTracking(true);
      processedAsanasRef.current.add(currentAsana.id);
      navigate('/pose-tracking', {
        state: {
          ...currentAsana,
          courseId: courseid,
          studentId: parsedStudentId,
          courseName: yogaAsanas[0]?.course_name || 'Yoga Course'
        }
      });
    } else {
      const nextIncompleteIndex = yogaAsanas.findIndex(
        (asana, index) => index > currentAsanaIndex && asana.completed !== 1 && !processedAsanasRef.current.has(asana.id)
      );
      if (nextIncompleteIndex !== -1) {
        console.log('Moving to next incomplete asana at index:', nextIncompleteIndex);
        setCurrentAsanaIndex(nextIncompleteIndex);
        setIsTracking(false);
      } else if (completedAsanas.length > 0) {
        console.log('No more incomplete asanas, navigating to summary with', completedAsanas.length, 'asanas');
        const allSessionData = completedAsanas
          .filter((asana) => asana.sessionData.averageAccuracy > 0)
          .map((asana) => ({
            poseName: asana.posturename,
            averageAccuracy: Number(asana.sessionData.averageAccuracy),
            captureDuration: Number(asana.sessionData.captureDuration),
            frameCount: Number(asana.sessionData.frameCount),
            goodPostureTime: Number(asana.sessionData.goodPostureTime),
            studentId: Number(asana.sessionData.studentId || parsedStudentId),
            courseId: Number(asana.sessionData.courseId || courseid),
            poseId: Number(asana.sessionData.poseId || asana.id),
            asanaimage: asana.sessionData.asanaimage
          }));
        console.log('Navigating to summary with allSessionData:', allSessionData);
        navigate(`/asanas/${courseid}/summary`, {
          state: {
            allSessionData,
            course: { courseName: yogaAsanas[0]?.course_name || 'Yoga Course' }
          }
        });
      } else {
        console.error('No completed asanas, redirecting to courses');
        toast.error('No completed asanas. Please complete at least one asana.');
        navigate('/student');
      }
    }
  };

  useEffect(() => {
    if (!yogaAsanas.length || isProcessing) return;

    const backendCompleted = yogaAsanas
      .filter((asana) => asana.completed === 1)
      .map((asana) => ({
        id: asana.id,
        posturename: asana.posturename,
        completedAt: new Date().toLocaleString(),
        sessionData: asana.sessionData || {
          averageAccuracy: 0,
          captureDuration: 0,
          frameCount: 0,
          goodPostureTime: 0,
          studentId: parsedStudentId,
          courseId: Number(courseid),
          poseId: asana.id,
          asanaimage: asana.asanaImage
        }
      }));

    setCompletedAsanas((prev) => {
      const mergedMap = new Map();
      [...prev, ...backendCompleted].forEach((asana) => {
        const existing = mergedMap.get(asana.id);
        if (!existing || asana.sessionData.averageAccuracy > 0) {
          mergedMap.set(asana.id, asana);
        }
      });
      const merged = Array.from(mergedMap.values());
      console.log('Synced completedAsanas:', merged);
      if (parsedStudentId && courseid) {
        localStorage.setItem(`completedAsanas_${courseid}_${parsedStudentId}`, JSON.stringify(merged));
      }
      const firstIncompleteIndex = yogaAsanas.findIndex((asana) => asana.completed !== 1 && !processedAsanasRef.current.has(asana.id));
      if (firstIncompleteIndex !== -1 && firstIncompleteIndex !== currentAsanaIndex) {
        console.log('Setting currentAsanaIndex to:', firstIncompleteIndex);
        setCurrentAsanaIndex(firstIncompleteIndex);
      } else if (firstIncompleteIndex === -1 && merged.length > 0 && !location.state?.sessionData) {
        console.log('All asanas completed on sync, navigating to summary');
        const allSessionData = merged
          .filter((asana) => asana.sessionData.averageAccuracy > 0)
          .map((asana) => ({
            poseName: asana.posturename,
            averageAccuracy: Number(asana.sessionData.averageAccuracy),
            captureDuration: Number(asana.sessionData.captureDuration),
            frameCount: Number(asana.sessionData.frameCount),
            goodPostureTime: Number(asana.sessionData.goodPostureTime),
            studentId: Number(asana.sessionData.studentId || parsedStudentId),
            courseId: Number(asana.sessionData.courseId || courseid),
            poseId: Number(asana.sessionData.poseId || asana.id),
            asanaimage: asana.sessionData.asanaimage
          }));
        console.log('Navigating to summary with allSessionData:', allSessionData);
        navigate(`/asanas/${courseid}/summary`, {
          state: {
            allSessionData,
            course: { courseName: yogaAsanas[0]?.course_name || 'Yoga Course' }
          }
        });
      } else if (firstIncompleteIndex === -1 && merged.length === 0) {
        console.error('No completed asanas on sync, redirecting to courses');
        toast.error('No completed asanas. Please complete at least one asana.');
        navigate('/student');
      }
      return merged;
    });
  }, [yogaAsanas, navigate, courseid, parsedStudentId, location.state]);

  useEffect(() => {
    if (isProcessing || isLoading || error || !yogaAsanas.length) {
      if (isLoading) console.log('Navigation skipped: Data is still loading');
      if (error) console.error('Navigation skipped: Error loading asanas:', error?.message);
      if (!yogaAsanas.length) console.log('Navigation skipped: No asanas available');
      return;
    }

    setIsProcessing(true);
    console.log('Processing asana:', {
      index: currentAsanaIndex,
      asana: yogaAsanas[currentAsanaIndex],
      isTracking,
      processed: processedAsanasRef.current.has(yogaAsanas[currentAsanaIndex]?.id),
      locationState: location.state
    });

    const currentAsana = yogaAsanas[currentAsanaIndex];
    if (!currentAsana) {
      console.error('Current asana undefined at index:', currentAsanaIndex);
      toast.error('Invalid asana data');
      navigate(location.pathname, { replace: true, state: {} });
      setIsProcessing(false);
      return;
    }

    if (location.state?.completed && location.state?.sessionData) {
      const sessionData = Array.isArray(location.state.sessionData)
        ? location.state.sessionData[0] // Take first item if array
        : location.state.sessionData;

      console.log('Received sessionData:', sessionData);

      if (!sessionData || processedAsanasRef.current.has(currentAsana.id)) {
        console.warn('No valid session data or asana already processed:', {
          sessionData,
          asanaId: currentAsana.id
        });
        setIsProcessing(false);
        navigateToNextAsana();
        return;
      }

      // Clear location.state immediately to prevent re-processing
      navigate(location.pathname, { replace: true, state: {} });

      (async () => {
        try {
          const asana = yogaAsanas.find((a) => a.id === Number(sessionData.poseId)) || currentAsana;
          console.log('Processing asana ID:', asana.id, 'with sessionData:', sessionData);

          const isValid =
            sessionData.poseId &&
            Number.isFinite(Number(sessionData.averageAccuracy)) &&
            Number(sessionData.averageAccuracy) > 0 &&
            Number.isFinite(Number(sessionData.captureDuration)) &&
            Number(sessionData.captureDuration) > 0 &&
            Number.isFinite(Number(sessionData.frameCount)) &&
            Number(sessionData.frameCount) > 0 &&
            Number.isFinite(Number(sessionData.goodPostureTime)) &&
            Number(sessionData.goodPostureTime) > 0 &&
            sessionData.studentLandmarks;

          if (!isValid) {
            console.warn('Invalid sessionData:', sessionData);
            toast.error('Invalid session data, continuing to next');
            setCurrentAsanaIndex((prev) => prev + 1);
            setIsProcessing(false);
            navigateToNextAsana();
            return;
          }

          const completedAsana = {
            id: asana.id,
            posturename: asana.posturename,
            completedAt: new Date().toLocaleString(),
            sessionData: {
              averageAccuracy: Number(sessionData.averageAccuracy),
              captureDuration: Number(sessionData.captureDuration),
              frameCount: Number(sessionData.frameCount),
              goodPostureTime: Number(sessionData.goodPostureTime),
              studentId: Number(parsedStudentId),
              courseId: Number(courseid),
              poseId: Number(asana.id),
              asanaimage: sessionData.asanaimage || asana.asanaImage,
              studentLandmarks: sessionData.studentLandmarks
            }
          };

          console.log('Sending report with asanaimage and landmarks:', {
            asanaimage: completedAsana.sessionData.asanaimage,
            studentLandmarks: completedAsana.sessionData.studentLandmarks,
            reportId: `report_${asana.id}_${Date.now()}` // Debug unique ID
          });

          processedAsanasRef.current.add(asana.id);
          console.log('Marked as processed:', asana.id, Array.from(processedAsanasRef.current));

          const completeAsanaResponse = await completeAsanaMutation.mutateAsync({
            studentId: parsedStudentId,
            courseId: Number(courseid),
            postureId: asana.id,
            asanaimage: sessionData.asanaimage,
            studentLandmarks: sessionData.studentLandmarks
          });

          await sendReportMutation.mutateAsync({
            studentId: Number(parsedStudentId),
            averageAccuracy: completedAsana.sessionData.averageAccuracy,
            captureDuration: completedAsana.sessionData.captureDuration,
            frameCount: completedAsana.sessionData.frameCount,
            goodPostureTime: completedAsana.sessionData.goodPostureTime,
            courseId: Number(courseid),
            poseId: Number(asana.id),
            asanaimage: completeAsanaResponse.asanaimage || sessionData.asanaimage,
            studentLandmarks: sessionData.studentLandmarks
          });

          setCompletedAsanas((prev) => {
            const mergedMap = new Map();
            [...prev, completedAsana].forEach((asana) => {
              const existing = mergedMap.get(asana.id);
              if (!existing || asana.sessionData.averageAccuracy > 0) {
                mergedMap.set(asana.id, asana);
              }
            });
            const updated = Array.from(mergedMap.values());
            console.log('Updated completedAsanas:', updated);
            if (parsedStudentId && courseid) {
              localStorage.setItem(`completedAsanas_${courseid}_${parsedStudentId}`, JSON.stringify(updated));
            }
            setIsProcessing(false);
            navigateToNextAsana();
            return updated;
          });
        } catch (error) {
          console.error('Mutation failed:', error);
          toast.error('Failed to process asana');
          setCurrentAsanaIndex((prev) => prev + 1);
          setIsProcessing(false);
          navigateToNextAsana();
        }
      })();
    } else {
      console.log('No completed state or sessionData, navigating to next asana');
      setIsProcessing(false);
      navigateToNextAsana();
    }

    // Cleanup to prevent double execution
    return () => {
      console.log('Cleaning up ContinuousFlow useEffect');
    };
  }, [isLoading, error, yogaAsanas, currentAsanaIndex, isTracking, location.state, isProcessing, navigate, parsedStudentId, courseid]);

  if (!parsedStudentId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Please log in to continue
        </Typography>
        <Button variant="contained" color="primary" onClick={() => navigate('/login')} sx={{ mt: 2, textTransform: 'none' }}>
          Go to Login
        </Button>
      </Box>
    );
  }

  if (isLoading) return <Box sx={{ p: 4, textAlign: 'center' }}>Loading...</Box>;
  if (error) return <Box sx={{ p: 4, textAlign: 'center' }}>Error loading asanas: {error.message}</Box>;
  if (!yogaAsanas.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography variant="h6" color="text.secondary">
          No asanas available for this course.
        </Typography>
      </Box>
    );
  }

  return null; // Navigation is automatic
}

export default ContinuousFlow;
